import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Lock, User, LogOut, Users, Building2, MessageSquare, 
  Trash2, ChevronDown, ChevronUp, RefreshCw, Download, FileText, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = "https://gaurav-o0un.onrender.com/api";

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('salesman');
  const [salesmanApps, setSalesmanApps] = useState([]);
  const [distributorEnqs, setDistributorEnqs] = useState([]);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);
      fetchAllData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.status === 'success') {
        localStorage.setItem('adminToken', response.data.token);
        setIsLoggedIn(true);
        toast.success('Login successful');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    toast.success('Logged out successfully');
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [salesmanRes, distributorRes, contactRes] = await Promise.all([
        axios.get(`${API}/admin/salesman-applications`),
        axios.get(`${API}/admin/distributor-enquiries`),
        axios.get(`${API}/admin/contact-messages`)
      ]);
      setSalesmanApps(salesmanRes.data.data || []);
      setDistributorEnqs(distributorRes.data.data || []);
      setContactMsgs(contactRes.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      let endpoint = '';
      if (type === 'salesman') endpoint = `/admin/salesman-application/${id}`;
      else if (type === 'distributor') endpoint = `/admin/distributor-enquiry/${id}`;
      else if (type === 'contact') endpoint = `/admin/contact-message/${id}`;
      
      await axios.delete(`${API}${endpoint}`);
      toast.success('Deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const FileDownloadButton = ({ type, id, fileType, fileData, label }) => {
    if (!fileData || !fileData.has_data) {
      return <span className="text-slate-400 text-sm">Not uploaded</span>;
    }
    
    const downloadUrl = `${API}/admin/download/${type}/${id}/${fileType}`;
    
    return (
      <div className="flex items-center gap-2">
        <a 
          href={downloadUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-[#0047AB] hover:underline text-sm"
        >
          <FileText className="w-4 h-4" />
          {fileData.filename}
        </a>
        <span className="text-xs text-slate-400">({formatFileSize(fileData.size)})</span>
        <a 
          href={downloadUrl} 
          download
          className="p-1 hover:bg-slate-100 rounded"
          title="Download"
        >
          <Download className="w-4 h-4 text-slate-600" />
        </a>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0047AB] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Admin Panel</h1>
            <p className="text-slate-600 text-sm mt-1">Gupta Soap Grih Udyog</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  placeholder="Enter username"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  placeholder="Enter password"
                  required
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-[#0047AB] hover:bg-[#003585]" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-[#0047AB] hover:underline">← Back to Website</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-[#0047AB] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Admin Panel</h1>
            <p className="text-blue-200 text-sm">Gupta Soap Grih Udyog</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={fetchAllData} 
              variant="outline" 
              size="sm" 
              className="border-white/30 text-white hover:bg-white/10"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 shadow-sm border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Salesman Applications</p>
                <p className="text-3xl font-bold text-slate-800">{salesmanApps.length}</p>
              </div>
              <Users className="w-12 h-12 text-blue-500 opacity-50" />
            </div>
          </div>
          <div className="bg-white p-6 shadow-sm border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Distributor Enquiries</p>
                <p className="text-3xl font-bold text-slate-800">{distributorEnqs.length}</p>
              </div>
              <Building2 className="w-12 h-12 text-green-500 opacity-50" />
            </div>
          </div>
          <div className="bg-white p-6 shadow-sm border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Contact Messages</p>
                <p className="text-3xl font-bold text-slate-800">{contactMsgs.length}</p>
              </div>
              <MessageSquare className="w-12 h-12 text-orange-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow-sm mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('salesman')}
              className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'salesman' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              <Users className="w-4 h-4" />
              Salesman ({salesmanApps.length})
            </button>
            <button
              onClick={() => setActiveTab('distributor')}
              className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'distributor' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              <Building2 className="w-4 h-4" />
              Distributor ({distributorEnqs.length})
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'contact' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              Contact ({contactMsgs.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Salesman Applications */}
          {activeTab === 'salesman' && (
            salesmanApps.length === 0 ? (
              <div className="bg-white p-12 text-center text-slate-500">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No salesman applications yet</p>
              </div>
            ) : (
              salesmanApps.map((app) => (
                <div key={app.id} className="bg-white shadow-sm">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleExpand(app.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{app.full_name}</p>
                        <p className="text-sm text-slate-500">{app.phone} • {app.city_state}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-slate-400">{formatDate(app.timestamp)}</span>
                      {expandedItems[app.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  {expandedItems[app.id] && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
                        <div><span className="text-xs text-slate-500 block">Full Name</span><p className="font-medium">{app.full_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Father's Name</span><p className="font-medium">{app.father_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Phone</span><p className="font-medium">{app.phone || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Email</span><p className="font-medium">{app.email || 'N/A'}</p></div>
                        <div className="md:col-span-2"><span className="text-xs text-slate-500 block">Address</span><p className="font-medium">{app.address || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">City/State</span><p className="font-medium">{app.city_state || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Education</span><p className="font-medium">{app.education || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Experience</span><p className="font-medium">{app.experience_years ? `${app.experience_years} years` : 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Previous Company</span><p className="font-medium">{app.previous_company || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Area Preference</span><p className="font-medium">{app.area_preference || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Expected Salary</span><p className="font-medium">{app.expected_salary ? `₹${app.expected_salary}` : 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Joining Date</span><p className="font-medium">{app.joining_date || 'N/A'}</p></div>
                      </div>
                      
                      {/* Documents Section */}
                      <div className="border-t pt-4 mt-2">
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> Uploaded Documents
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-3 bg-white rounded border">
                            <span className="text-xs text-slate-500 block mb-1">Resume</span>
                            <FileDownloadButton type="salesman" id={app.id} fileType="resume" fileData={app.resume} />
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <span className="text-xs text-slate-500 block mb-1">Aadhar Card</span>
                            <FileDownloadButton type="salesman" id={app.id} fileType="aadhar_card" fileData={app.aadhar_card} />
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <span className="text-xs text-slate-500 block mb-1">Experience Certificate</span>
                            <FileDownloadButton type="salesman" id={app.id} fileType="experience_certificate" fileData={app.experience_certificate} />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end pt-4 border-t mt-4">
                        <Button 
                          onClick={(e) => { e.stopPropagation(); deleteItem('salesman', app.id); }}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )
          )}

          {/* Distributor Enquiries */}
          {activeTab === 'distributor' && (
            distributorEnqs.length === 0 ? (
              <div className="bg-white p-12 text-center text-slate-500">
                <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No distributor enquiries yet</p>
              </div>
            ) : (
              distributorEnqs.map((enq) => (
                <div key={enq.id} className="bg-white shadow-sm">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleExpand(enq.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{enq.firm_name}</p>
                        <p className="text-sm text-slate-500">{enq.owner_name} • {enq.area_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-slate-400">{formatDate(enq.timestamp)}</span>
                      {expandedItems[enq.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  {expandedItems[enq.id] && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
                        <div><span className="text-xs text-slate-500 block">Firm Name</span><p className="font-medium">{enq.firm_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Owner Name</span><p className="font-medium">{enq.owner_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Area</span><p className="font-medium">{enq.area_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Phone</span><p className="font-medium">{enq.phone || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">GSTIN</span><p className="font-medium">{enq.gstin || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Aadhar Number</span><p className="font-medium">{enq.aadhar_number || 'N/A'}</p></div>
                        <div className="md:col-span-2"><span className="text-xs text-slate-500 block">Firm Address</span><p className="font-medium">{enq.firm_address || 'N/A'}</p></div>
                        <div className="md:col-span-2"><span className="text-xs text-slate-500 block">Godown Address</span><p className="font-medium">{enq.godown_address || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Year Established</span><p className="font-medium">{enq.year_of_establishment || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Other Products</span><p className="font-medium">{enq.other_products || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Last Year Turnover</span><p className="font-medium">{enq.last_year_turnover ? `₹${enq.last_year_turnover}` : 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Delivery Mode</span><p className="font-medium">{enq.delivery_mode || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Areas Covered</span><p className="font-medium">{enq.areas_covered || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">No. of Salesmen</span><p className="font-medium">{enq.num_salesmen || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Bank Name</span><p className="font-medium">{enq.bank_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Branch Name</span><p className="font-medium">{enq.branch_name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Account Number</span><p className="font-medium">{enq.account_number || 'N/A'}</p></div>
                      </div>
                      
                      {/* Documents Section */}
                      <div className="border-t pt-4 mt-2">
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> Uploaded Documents
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-3 bg-white rounded border">
                            <span className="text-xs text-slate-500 block mb-1">GST Certificate</span>
                            <FileDownloadButton type="distributor" id={enq.id} fileType="gst_certificate" fileData={enq.gst_certificate} />
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <span className="text-xs text-slate-500 block mb-1">Trade License</span>
                            <FileDownloadButton type="distributor" id={enq.id} fileType="trade_license" fileData={enq.trade_license} />
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <span className="text-xs text-slate-500 block mb-1">Supporting Documents</span>
                            <FileDownloadButton type="distributor" id={enq.id} fileType="supporting_docs" fileData={enq.supporting_docs} />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end pt-4 border-t mt-4">
                        <Button 
                          onClick={(e) => { e.stopPropagation(); deleteItem('distributor', enq.id); }}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )
          )}

          {/* Contact Messages */}
          {activeTab === 'contact' && (
            contactMsgs.length === 0 ? (
              <div className="bg-white p-12 text-center text-slate-500">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No contact messages yet</p>
              </div>
            ) : (
              contactMsgs.map((msg) => (
                <div key={msg.id} className="bg-white shadow-sm">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                    onClick={() => toggleExpand(msg.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{msg.name}</p>
                        <p className="text-sm text-slate-500">{msg.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-slate-400">{formatDate(msg.timestamp)}</span>
                      {expandedItems[msg.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  {expandedItems[msg.id] && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
                        <div><span className="text-xs text-slate-500 block">Name</span><p className="font-medium">{msg.name || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Email</span><p className="font-medium">{msg.email || 'N/A'}</p></div>
                        <div><span className="text-xs text-slate-500 block">Phone</span><p className="font-medium">{msg.phone || 'N/A'}</p></div>
                        <div className="md:col-span-3">
                          <span className="text-xs text-slate-500 block">Message</span>
                          <p className="font-medium bg-white p-3 mt-1 border rounded">{msg.message || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex justify-end pt-2 border-t mt-2">
                        <Button 
                          onClick={(e) => { e.stopPropagation(); deleteItem('contact', msg.id); }}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
